import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { Event, Fight, FightResult } from '@/data/events'
import { events as eventsData } from '@/data/events'
import { api } from '@/api'
import { getRealtimeManager, type RealtimeMessage } from '@/composables/useRealtime'
import type { LiveFightStatus, FighterLiveStats } from '@/types'

// Re-export types for compatibility
export type { LiveFightStatus, FighterLiveStats }

// Legacy interface kept for compatibility
export interface LiveFightUpdate {
  eventId: number
  fightId: number
  status: 'in_progress' | 'completed'
  result?: FightResult
  currentRound?: number
  timeElapsed?: string
}

export const useEventsStore = defineStore('events', () => {
  const events = ref<Event[]>(eventsData)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastUpdated = ref<number | null>(null)
  const isRealtimeConnected = ref(false)
  const activeFight = ref<LiveFightUpdate | null>(null)
  // Enhanced live fight status with detailed stats
  const activeLiveFight = ref<LiveFightStatus | null>(null)

  // Real-time subscription cleanup
  let unsubscribeEvents: (() => void) | null = null
  let unsubscribeEventUpdate: (() => void) | null = null
  let unsubscribeFightUpdate: (() => void) | null = null
  let unsubscribeLiveUpdate: (() => void) | null = null
  let unsubscribeLiveFightUpdate: (() => void) | null = null
  let unsubscribeStateChange: (() => void) | null = null

  const upcomingEvents = computed(() => {
    return events.value.filter(e => e.status === 'upcoming')
  })

  const completedEvents = computed(() => {
    return events.value.filter(e => e.status === 'completed')
  })

  const liveEvents = computed(() => {
    return events.value.filter(e => e.status === 'live')
  })

  const hasLiveEvents = computed(() => liveEvents.value.length > 0)

  // Watch for live events and adjust polling frequency automatically
  watch(hasLiveEvents, (hasLive) => {
    const manager = getRealtimeManager()
    manager.setLiveEventMode(hasLive)
  }, { immediate: true })

  async function fetchEvents(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const data = await api.get<Event[]>('/events')
      events.value = data
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch events'
      error.value = message
      // Keep existing data on error (static fallback)
    } finally {
      isLoading.value = false
    }
  }

  function getEventById(id: number): Event | undefined {
    return events.value.find(e => e.id === id)
  }

  async function fetchEventById(id: number): Promise<Event | undefined> {
    isLoading.value = true
    error.value = null

    try {
      const event = await api.get<Event>(`/events/${id}`)
      // Update the event in the local array if it exists
      const index = events.value.findIndex(e => e.id === id)
      if (index >= 0) {
        events.value[index] = event
      } else {
        events.value.push(event)
      }
      return event
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch event'
      error.value = message
      // Return from static data on error
      return getEventById(id)
    } finally {
      isLoading.value = false
    }
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  function formatDateLong(dateStr: string): string {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  function getStatusClass(status: Event['status']): string {
    switch (status) {
      case 'live':
        return 'bg-ufc-red animate-pulse'
      case 'upcoming':
        return 'bg-ufc-gold text-black'
      case 'completed':
        return 'bg-gray-600'
      default:
        return 'bg-gray-600'
    }
  }

  function clearError(): void {
    error.value = null
  }

  /**
   * Subscribe to real-time event updates
   */
  function subscribeToRealtime(): void {
    const manager = getRealtimeManager()

    // Subscribe to bulk events updates (from polling fallback)
    unsubscribeEvents = manager.on<Event[]>('events_update', (message: RealtimeMessage<Event[]>) => {
      events.value = message.data
      lastUpdated.value = message.timestamp || Date.now()
    })

    // Subscribe to individual event updates
    unsubscribeEventUpdate = manager.on<Event>('event_update', (message: RealtimeMessage<Event>) => {
      const updatedEvent = message.data
      const index = events.value.findIndex(e => e.id === updatedEvent.id)
      if (index >= 0) {
        events.value[index] = updatedEvent
      } else {
        events.value.push(updatedEvent)
      }
      lastUpdated.value = message.timestamp || Date.now()
    })

    // Subscribe to fight updates within events
    unsubscribeFightUpdate = manager.on<{ eventId: number; fight: Fight }>('fight_update', (message) => {
      const { eventId, fight } = message.data
      const eventIndex = events.value.findIndex(e => e.id === eventId)
      if (eventIndex >= 0) {
        const fightIndex = events.value[eventIndex].fights.findIndex(f => f.id === fight.id)
        if (fightIndex >= 0) {
          events.value[eventIndex].fights[fightIndex] = fight
        }
      }
      lastUpdated.value = message.timestamp || Date.now()
    })

    // Subscribe to live fight updates (round-by-round, real-time scoring, etc.)
    unsubscribeLiveUpdate = manager.on<LiveFightUpdate>('live_update', (message: RealtimeMessage<LiveFightUpdate>) => {
      activeFight.value = message.data

      // Update event status to live if not already
      const eventIndex = events.value.findIndex(e => e.id === message.data.eventId)
      if (eventIndex >= 0 && events.value[eventIndex].status !== 'live') {
        events.value[eventIndex].status = 'live'
      }

      // Update fight result if completed
      if (message.data.status === 'completed' && message.data.result) {
        const event = events.value[eventIndex]
        if (event) {
          const fightIndex = event.fights.findIndex(f => f.id === message.data.fightId)
          if (fightIndex >= 0) {
            event.fights[fightIndex].result = message.data.result
          }
        }
        // Clear active fight if it's the same one
        if (activeFight.value?.fightId === message.data.fightId) {
          activeFight.value = null
        }
      }

      lastUpdated.value = message.timestamp || Date.now()
    })

    // Subscribe to enhanced live fight updates with detailed stats
    unsubscribeLiveFightUpdate = manager.on<LiveFightStatus>('live_fight_update', (message: RealtimeMessage<LiveFightStatus>) => {
      const liveFight = message.data
      activeLiveFight.value = liveFight

      // Update event status to live if fight is in progress
      if (liveFight.liveStatus === 'in_round' || liveFight.liveStatus === 'between_rounds') {
        const eventIndex = events.value.findIndex(e => e.id === liveFight.eventId)
        if (eventIndex >= 0 && events.value[eventIndex].status !== 'live') {
          events.value[eventIndex].status = 'live'
        }
      }

      // Clear active live fight if completed
      if (liveFight.liveStatus === 'completed' || liveFight.liveStatus === 'cancelled') {
        // Keep it briefly for final state display, then clear
        setTimeout(() => {
          if (activeLiveFight.value?.fightId === liveFight.fightId) {
            activeLiveFight.value = null
          }
        }, 5000)
      }

      lastUpdated.value = message.timestamp || Date.now()
    })

    // Track connection state
    unsubscribeStateChange = manager.onStateChange((state) => {
      isRealtimeConnected.value = state === 'connected'
    })
  }

  /**
   * Unsubscribe from real-time updates
   */
  function unsubscribeFromRealtime(): void {
    unsubscribeEvents?.()
    unsubscribeEventUpdate?.()
    unsubscribeFightUpdate?.()
    unsubscribeLiveUpdate?.()
    unsubscribeLiveFightUpdate?.()
    unsubscribeStateChange?.()
    unsubscribeEvents = null
    unsubscribeEventUpdate = null
    unsubscribeFightUpdate = null
    unsubscribeLiveUpdate = null
    unsubscribeLiveFightUpdate = null
    unsubscribeStateChange = null
  }

  /**
   * Update a single event in the store (for optimistic updates)
   */
  function updateEvent(event: Event): void {
    const index = events.value.findIndex(e => e.id === event.id)
    if (index >= 0) {
      events.value[index] = event
    } else {
      events.value.push(event)
    }
    lastUpdated.value = Date.now()
  }

  /**
   * Clear active fight data
   */
  function clearActiveFight(): void {
    activeFight.value = null
  }

  /**
   * Clear active live fight data
   */
  function clearActiveLiveFight(): void {
    activeLiveFight.value = null
  }

  /**
   * Fetch live fight status for a specific fight
   */
  async function fetchLiveFightStatus(fightId: number): Promise<LiveFightStatus | null> {
    try {
      const data = await api.get<LiveFightStatus>(`/live/fights/${fightId}`)
      activeLiveFight.value = data
      return data
    } catch {
      return null
    }
  }

  /**
   * Fetch all live fights for an event
   */
  async function fetchEventLiveFights(eventId: number): Promise<LiveFightStatus[]> {
    try {
      return await api.get<LiveFightStatus[]>(`/events/${eventId}/live`)
    } catch {
      return []
    }
  }

  return {
    // State
    events,
    isLoading,
    error,
    lastUpdated,
    isRealtimeConnected,
    activeFight,
    activeLiveFight,
    // Getters
    upcomingEvents,
    completedEvents,
    liveEvents,
    hasLiveEvents,
    // Actions
    fetchEvents,
    fetchEventById,
    getEventById,
    formatDate,
    formatDateLong,
    getStatusClass,
    clearError,
    // Real-time
    subscribeToRealtime,
    unsubscribeFromRealtime,
    updateEvent,
    clearActiveFight,
    clearActiveLiveFight,
    fetchLiveFightStatus,
    fetchEventLiveFights
  }
})

export type { Event } from '@/data/events'
export type { Fight } from '@/data/events'
export type { FightResult } from '@/data/events'
